import * as RadixPopover from '@radix-ui/react-popover';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  PropsWithChildren,
} from 'react';

import { keyframes, styled, theme } from '../../stitches.config';

type PopoverProps = PropsWithChildren<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modal?: boolean;
}>;

type PopupProps = ComponentPropsWithoutRef<typeof RadixPopover.Content> & {
  align?: 'end' | 'center' | 'start';
  hideArrow?: boolean;
  arrowColor?: string;
  container?: HTMLElement;
};
interface AnchorProps {
  noGap?: boolean;
}

const Anchor = styled(
  forwardRef<HTMLDivElement, AnchorProps & PropsWithChildren<unknown>>(
    (props, ref) => {
      const { noGap } = props;
      return (
        <AnchorContainer {...props} ref={ref}>
          <AnchorView noGap={noGap} />
          {props.children}
        </AnchorContainer>
      );
    },
  ),
);
Anchor.displayName = 'Popover.Anchor';

type VirtualAnchorProps = {
  /**
   * A virtual element (`{ getBoundingClientRect() }`) to anchor the popover to,
   * for positioning against a rect that has no dedicated DOM node — e.g. a text
   * selection or a specific wrapped line. The popup (and its collision
   * flip/shift) then positions relative to that rect.
   */
  virtualRef: ComponentPropsWithoutRef<
    typeof RadixPopover.Anchor
  >['virtualRef'];
};

const VirtualAnchor = ({ virtualRef }: VirtualAnchorProps) => (
  <RadixPopover.Anchor virtualRef={virtualRef} />
);
VirtualAnchor.displayName = 'Popover.VirtualAnchor';

const Popup = styled(
  forwardRef<ElementRef<typeof RadixPopover.Content>, PopupProps>(
    (props, ref) => {
      const { align, hideArrow, arrowColor, container, ...rest } = props;
      return (
        <RadixPopover.Portal container={container}>
          <Content
            ref={ref}
            {...rest}
            align={props.align}
            arrowPadding={hideArrow ? 0 : 6}
          >
            {!hideArrow && (
              <>
                <ArrowBorder />
                <Arrow style={{ fill: arrowColor ?? 'white' }} />
              </>
            )}
            {props.children}
          </Content>
        </RadixPopover.Portal>
      );
    },
  ),
);
Popup.displayName = 'Popover.Popup';

const Root = (props: PopoverProps) => {
  const { children, modal = true, ...rest } = props;
  return (
    <RadixPopover.Root modal={modal} {...rest}>
      {children}
    </RadixPopover.Root>
  );
};
Root.displayName = 'Popover.Root';

export const Popover = { Anchor, VirtualAnchor, Root, Popup };

const scaleIn = keyframes({
  from: { transform: 'rotateX(-30deg) scale(0.9)', opacity: 0 },
  to: { transform: 'rotateX(0deg) scale(1)', opacity: 1 },
});

const scaleOut = keyframes({
  from: { transform: 'rotateX(0deg) scale(1)', opacity: 1 },
  to: { transform: 'rotateX(-10deg) scale(0.95)', opacity: 0 },
});

const Content = styled(RadixPopover.Content, {
  backgroundColor: 'white',
  borderRadius: 6,
  border: `1px solid ${theme.colors.gray7}`,
  zIndex: 1000,
  transformOrigin: 'top center',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  height: 'var(--radix-navigation-menu-viewport-height)',
  // Ensure interactivity when rendered inside portal containers with pointer-events: none
  pointerEvents: 'auto',

  '@media only screen and (min-width: 600px)': {
    width: 'var(--radix-navigation-menu-viewport-width)',
  },
  '@media (prefers-reduced-motion: no-preference)': {
    transition: 'width, height, 300ms ease',
    '&[data-state="open"]': { animation: `${scaleIn} 200ms ease` },
    '&[data-state="closed"]': { animation: `${scaleOut} 200ms ease` },
  },
});

const Arrow = styled(RadixPopover.Arrow, {
  transform: 'translateY(-2px)',
  zIndex: 1000,
});
const ArrowBorder = styled(RadixPopover.Arrow, {
  fill: theme.colors.gray7,
  zIndex: 1000,
});

const AnchorContainer = styled('div', {
  position: 'relative',
  display: 'inline-block',
});

const AnchorView = styled(RadixPopover.Anchor, {
  position: 'absolute',
  left: -6,
  right: -6,
  top: -6,
  bottom: -6,
  pointerEvents: 'none',
  variants: {
    noGap: {
      true: {
        bottom: 0,
      },
    },
  },
});
